/* ─── graphBuilder ───────────────────────────────────────────────────
   Construit un graphe de nœuds/arêtes (pages + décisions + conséquences)
   à partir d'un game_config, pour la vue "Graphe de l'atelier".
   Fonction pure, sans dépendance React ni @xyflow — juste de la donnée.

   Structure produite : un TRONC (les pages de `config.pile`, depth 0) sur
   lequel se greffent des COULOIRS de conséquences (depth 1, 2, …). C'est
   cette profondeur qui pilote le layout en colonnes (cf. graphLayout.js).

   Une même page peut être référencée à plusieurs endroits du flux (ex: une
   page de transition générique réutilisée comme conséquence de plusieurs
   décisions différentes). Pour rester lisible, chaque occurrence dans le
   flux devient un nœud distinct (id composite `${pageId}::${n}`) plutôt
   qu'un unique nœud "carrefour" avec de nombreuses arêtes convergentes.
   Un cycle réel (une conséquence qui referme la boucle sur une page déjà
   visitée dans le même chemin) arrête la duplication à cet endroit.

   Chaque arête porte une `ref` : la localisation EXACTE de la conséquence
   dans la base (quel document, quel tableau, quel index). C'est ce qui
   permet à la vue graphe d'éditer/supprimer une conséquence sans avoir à
   deviner où elle est rangée.
*/

import { authoredDecisionIds, isPoolPage } from "./measurePool";

// Résout le consequence_id effectif d'une conséquence de décision pour un atelier donné :
// variation_game_config[configId] écrase consequence_id si présent (null = "rien à ajouter").
export function resolveConsequenceId(consequence, configId) {
  const variations = consequence.variation_game_config || {};
  if (configId in variations) return variations[configId] ?? null;
  return consequence.consequence_id ?? null;
}

// Une conséquence est-elle pilotée, pour cet atelier, par une variante plutôt
// que par le champ générique ? (déterminant pour savoir quel champ éditer)
export function isVariationDriven(consequence, configId) {
  return configId in (consequence.variation_game_config || {});
}

// Condition lisible + type, pour affichage sur l'arête (même logique que ConsequenceSummary).
export function describeCondition(consequence, { decisionLabel, scoreLabel }) {
  if (!consequence.condition) return { text: "Toujours", conditional: false };
  const meta = consequence.condition_metadata;
  if (meta === undefined) {
    // Modèle "décision" simplifié : condition = decision précédente directement
    return {
      text: consequence.previous_decision_id
        ? `Si « ${decisionLabel(consequence.previous_decision_id)} » a été prise`
        : "Si une décision précédente a été prise",
      conditional: true,
    };
  }
  if (meta.type === "past_decision") {
    return {
      text: meta.previous_decision_id
        ? `Si « ${decisionLabel(meta.previous_decision_id)} » a été prise`
        : "Si une décision précédente a été prise",
      conditional: true,
    };
  }
  if (meta.type === "score_below") {
    return { text: `Si « ${scoreLabel(meta.score_id) || "?"} » ≤ ${meta.value ?? "?"}`, conditional: true };
  }
  if (meta.type === "score_above") {
    return { text: `Si « ${scoreLabel(meta.score_id) || "?"} » > ${meta.value ?? "?"}`, conditional: true };
  }
  return { text: "Condition", conditional: true };
}

// Identifiants des ports (handles) React Flow. Encodent l'origine réelle de
// la branche : une mesure précise et son côté (prise / non prise), ou la page.
export const handleForDecision = (decisionId, side) => `dec::${decisionId}::${side}`;
export const PAGE_HANDLE = "page::general";

// Ports de séquence : le tronc s'enchaîne verticalement (bas → haut), les
// conséquences partent latéralement (droite → gauche). Deux axes distincts,
// sinon un lien de pile doit contourner la carte pour la rejoindre.
export const SEQ_SOURCE_HANDLE = "seq::bottom";
export const SEQ_TARGET_HANDLE = "seq::top";
export const CONS_TARGET_HANDLE = "cons::left";

export function parseHandle(handleId) {
  if (!handleId || handleId === PAGE_HANDLE) return { kind: "page" };
  if (!handleId.startsWith("dec::")) return { kind: "page" };
  const [, decisionId, side] = handleId.split("::");
  return { kind: "decision", decisionId, side };
}

const PAGE_TYPES_WITH_DECISIONS = new Set(["mosaic", "one-choice", "command_control"]);

/**
 * buildGraph(config, allPages, allDecisions)
 * → { nodes, edges, childrenOf, parentOf, decisionUsageInGraph }
 *
 * nodes : une entrée par occurrence de page dans le flux.
 *   { id, pageId, page, missing, inPile, pileIndex, depth, parentId,
 *     rows: [{ decisionId, decision, title, takenCount, notTakenCount }],
 *     hasDecisions, outgoingCount }
 *
 * edges.kind : "sequence" | "decision-taken" | "decision-not-taken" | "page-consequence"
 * edges.ref  : { doc: "decision"|"page", id, list, index }
 */
export function buildGraph(config, allPages, allDecisions) {
  const pageById = new Map(allPages.map((p) => [p._id, p]));
  const decisionById = new Map(allDecisions.map((d) => [d._id, d]));
  const decisionLabel = (id) => decisionById.get(id)?.title || id;
  const scoreLabel = (id) => (config.score_dic || {})[id]?.name || id;
  const configId = config._id;

  const pile = config.pile || [];
  const nodesById = new Map();
  const edges = [];
  let edgeSeq = 0;
  let occurrenceSeq = 0;
  const nextNodeId = (pageId) => `${pageId}::${occurrenceSeq++}`;

  const makeNode = (nodeId, pageId, { inPile, pileIndex, depth, parentId }) => {
    const page = pageById.get(pageId);
    // UNE PAGE QUI PIOCHE MONTRE TOUT SON RÉSERVOIR. On ne sait pas laquelle
    // des mesures sortira : les afficher toutes est la seule lecture honnête
    // du parcours, et c'est aussi ce qui garde leurs conséquences dans le
    // graphe — sans quoi les pages qu'elles déclenchent sembleraient
    // injoignables.
    const rows = authoredDecisionIds(page, config).map((decId) => {
      const decision = decisionById.get(decId);
      return {
        decisionId: decId,
        decision: decision || null,
        title: decision?.title || decId,
        missing: !decision,
        // Remplis par la passe d'indexation en fin de buildGraph : la carte
        // dépliée affiche ses conséquences en propre, sans passer par une arête.
        taken: [],
        notTaken: [],
        takenCount: 0,
        notTakenCount: 0,
      };
    });
    const node = {
      id: nodeId,
      pageId,
      page: page || null,
      missing: !page,
      inPile,
      pileIndex: pileIndex ?? null,
      depth,
      parentId: parentId ?? null,
      rows,
      hasDecisions: PAGE_TYPES_WITH_DECISIONS.has(page?.type),
      // Une page qui pioche : ses mesures sont celles du réservoir, et le
      // parcours réel n'en montrera qu'une partie, différente à chaque séance.
      // Le graphe doit le dire, sinon il se lit comme un déroulé certain.
      isPool: isPoolPage(page),
      pageConsequenceCount: (page?.page_general_consequence || []).length,
      outgoingCount: 0,
    };
    nodesById.set(nodeId, node);
    return node;
  };

  // 1) Tronc : les pages de la pile, une occurrence par position.
  const pileNodeIds = pile.map((pageId, i) => {
    const nodeId = nextNodeId(pageId);
    makeNode(nodeId, pageId, { inPile: true, pileIndex: i, depth: 0, parentId: null });
    return nodeId;
  });
  for (let i = 0; i < pileNodeIds.length - 1; i++) {
    edges.push({
      id: `seq-${i}`,
      source: pileNodeIds[i],
      target: pileNodeIds[i + 1],
      sourceHandle: SEQ_SOURCE_HANDLE,
      targetHandle: SEQ_TARGET_HANDLE,
      kind: "sequence",
      label: null,
      conditional: false,
      // Position d'insertion si l'auteur clique le « + » porté par ce lien.
      insertAtIndex: i + 1,
      ref: null,
    });
  }

  // 2) Parcours en profondeur depuis chaque page de la pile pour découvrir
  //    les conséquences. `pathSeen` détecte les cycles sur le chemin courant.
  const explore = (nodeId, pageId, pathSeen) => {
    const page = pageById.get(pageId);
    if (!page) return;
    if (pathSeen.has(pageId)) return;
    const nextPathSeen = new Set(pathSeen).add(pageId);
    const sourceNode = nodesById.get(nodeId);

    const emitConsequence = ({ targetPageId, kind, sourceHandle, decisionId, decisionTitle, text, conditional, ref, variationDriven }) => {
      const missing = !pageById.has(targetPageId);
      const targetNodeId = nextNodeId(targetPageId);
      if (!missing) {
        makeNode(targetNodeId, targetPageId, {
          inPile: false, pileIndex: null, depth: sourceNode.depth + 1, parentId: nodeId,
        });
      } else {
        nodesById.set(targetNodeId, {
          id: targetNodeId, pageId: targetPageId, page: null, missing: true,
          inPile: false, pileIndex: null, depth: sourceNode.depth + 1, parentId: nodeId,
          rows: [], hasDecisions: false, pageConsequenceCount: 0, outgoingCount: 0,
        });
      }

      edges.push({
        id: `cons-${edgeSeq++}`,
        source: nodeId,
        target: targetNodeId,
        sourceHandle,
        targetHandle: CONS_TARGET_HANDLE,
        kind,
        decisionId,
        decisionLabel: decisionTitle,
        label: text,
        conditional,
        missing,
        variationDriven,
        ref,
      });
      sourceNode.outgoingCount += 1;

      if (!missing) explore(targetNodeId, targetPageId, nextPathSeen);
    };

    // 2a) Conséquences des décisions de cette page
    sourceNode.rows.forEach((row) => {
      const decision = row.decision;
      if (!decision) return;

      const emitList = (listName, kind, side) => {
        (decision[listName] || []).forEach((consequence, index) => {
          const targetPageId = resolveConsequenceId(consequence, configId);
          if (!targetPageId) return;
          const { text, conditional } = describeCondition(consequence, { decisionLabel, scoreLabel });
          emitConsequence({
            targetPageId,
            kind,
            sourceHandle: handleForDecision(row.decisionId, side),
            decisionId: row.decisionId,
            decisionTitle: decision.title || row.decisionId,
            text,
            conditional,
            variationDriven: isVariationDriven(consequence, configId),
            ref: { doc: "decision", id: row.decisionId, list: listName, index },
          });
        });
      };
      emitList("add_elem_to_pile_if_taken", "decision-taken", "taken");
      emitList("add_elem_to_pile_if_not_taken", "decision-not-taken", "nottaken");
    });

    // 2b) Conséquences générales de la page elle-même
    (page.page_general_consequence || []).forEach((consequence, index) => {
      const targetPageId = consequence.consequence_id;
      if (!targetPageId) return;
      const { text, conditional } = describeCondition(consequence, { decisionLabel, scoreLabel });
      emitConsequence({
        targetPageId,
        kind: "page-consequence",
        sourceHandle: PAGE_HANDLE,
        decisionId: undefined,
        decisionTitle: undefined,
        text,
        conditional,
        variationDriven: false,
        ref: { doc: "page", id: pageId, list: "page_general_consequence", index },
      });
    });
  };

  pile.forEach((pageId, i) => explore(pileNodeIds[i], pageId, new Set()));

  const nodes = Array.from(nodesById.values());

  // Rattache chaque conséquence à SA ligne de mesure. La carte dépliée s'en
  // sert pour afficher les conséquences dans un cadre aligné sous la mesure,
  // sans tracer d'arête — sauf pour celles qui enchaînent sur autre chose
  // (`chained`), qu'on ne peut pas contenir sans mentir sur le déroulé.
  edges.forEach((e) => {
    if (!e.decisionId) return;
    const row = nodesById.get(e.source)?.rows.find((r) => r.decisionId === e.decisionId);
    if (!row) return;
    const target = nodesById.get(e.target);
    const entry = {
      edgeId: e.id,
      targetNodeId: e.target,
      targetPageId: target?.pageId,
      targetTitle: target?.page?.title || target?.pageId,
      targetType: target?.page?.type,
      missing: e.missing,
      conditional: e.conditional,
      label: e.label,
      ref: e.ref,
      variationDriven: e.variationDriven,
      chained: !!target && !target.missing && (target.outgoingCount > 0 || target.hasDecisions),
    };
    if (e.kind === "decision-taken") row.taken.push(entry);
    else row.notTaken.push(entry);
  });
  nodes.forEach((n) => n.rows.forEach((r) => {
    r.takenCount = r.taken.length;
    r.notTakenCount = r.notTaken.length;
  }));

  // Index de navigation : enfants / parent, hors arêtes de séquence (le tronc
  // n'est pas une relation de parenté mais un ordre).
  const childrenOf = new Map(nodes.map((n) => [n.id, []]));
  const parentOf = new Map();
  edges.forEach((e) => {
    if (e.kind === "sequence") return;
    childrenOf.get(e.source)?.push(e.target);
    parentOf.set(e.target, e.source);
  });

  return { nodes, edges, childrenOf, parentOf };
}

/* ─── Sous-arbre / ancêtres ─────────────────────────────────────────
   Utilisés par le mode focus : on éclaire une branche et tout ce qu'elle
   entraîne, plus le chemin qui y mène.
*/
export function collectSubtree(startNodeIds, childrenOf) {
  const out = new Set();
  const stack = [...startNodeIds];
  while (stack.length) {
    const id = stack.pop();
    if (out.has(id)) continue;
    out.add(id);
    (childrenOf.get(id) || []).forEach((c) => stack.push(c));
  }
  return out;
}

export function collectAncestors(nodeId, parentOf) {
  const out = new Set();
  let cur = parentOf.get(nodeId);
  while (cur && !out.has(cur)) {
    out.add(cur);
    cur = parentOf.get(cur);
  }
  return out;
}

/* ─── Usage partagé entre ateliers ──────────────────────────────────
   Une page/décision éditée depuis le graphe d'un atelier est un document
   PARTAGÉ : la modifier peut changer le déroulé d'autres ateliers. On
   calcule ici, côté client, quels ateliers atteignent réellement chaque
   page et chaque décision, pour pouvoir l'afficher sur les nœuds/lignes.
   (Équivalent léger de find_configs_using côté serveur, mais pour toutes
   les entités d'un coup plutôt qu'une par requête.)
*/
export function computeSharedUsage(configs, allPages, allDecisions) {
  const pageById = new Map(allPages.map((p) => [p._id, p]));
  const decisionById = new Map(allDecisions.map((d) => [d._id, d]));
  const pageUsage = new Map();
  const decisionUsage = new Map();

  const addUsage = (map, key, configId) => {
    if (!map.has(key)) map.set(key, new Set());
    map.get(key).add(configId);
  };

  configs.forEach((cfg) => {
    const seen = new Set();
    const queue = [...(cfg.pile || [])];
    while (queue.length) {
      const pageId = queue.shift();
      if (!pageId || seen.has(pageId)) continue;
      seen.add(pageId);
      addUsage(pageUsage, pageId, cfg._id);

      const page = pageById.get(pageId);
      if (!page) continue;

      authoredDecisionIds(page, cfg).forEach((decId) => {
        addUsage(decisionUsage, decId, cfg._id);
        const decision = decisionById.get(decId);
        if (!decision) return;
        ["add_elem_to_pile_if_taken", "add_elem_to_pile_if_not_taken"].forEach((list) => {
          (decision[list] || []).forEach((c) => {
            const target = resolveConsequenceId(c, cfg._id);
            if (target) queue.push(target);
          });
        });
      });

      (page.page_general_consequence || []).forEach((c) => {
        if (c.consequence_id) queue.push(c.consequence_id);
      });
    }
  });

  return { pageUsage, decisionUsage };
}
